const moment = require('moment');
const { get, cloneDeep } = require('lodash');

const { getApiBase } = require('./api.js');
const { getProductMetadata, clientFormatOfProductMetadata } = require('./productMetadata.js');
const { selfDefinedUserTypes } = require('./user.js');

const COURSE_FOLDERS = {
  GET_STARTED: 'getStarted',
  PROFESSIONAL_DEVELOPMENT: 'professionalDevelopment'
};

const FOLDER_ITEMS = {
  RESOURCES: 'resources',
  TITLE: 'title',
  KEYWORDS: 'keywords'
};

const COURSE_CONSTANTS = {
  EVALUATION_MODE_LOCKED: 'evaluationModeLocked',
  NORMAL_MODE_HIDDEN: 'normalModeHidden'
};
const COURSES_PROP = 'courses';
const ENTITLEMENTS_PROP = 'entitlements';
const MY_OTHER_PRODUCTS_COURSE_ID = 'myOtherProducts';

const resourceFlags = {
  // original potentially unclear form used in LOR inputs
  // in fact strictly LOR had an input "teacher" and output
  // "teacherResource"
  teacherFlag: 'teacherResource',

  // more explicitly exclusive format used in EPS course inputs
  // which is equivalent in function to teacherResource
  teacherOnlyFlag: 'teacherOnly'
};

const specialPackIsbns = {
  'BUZZ-LEVEL-1': {
    eval: '9780194151153',
    nonEval: '9780194865067'
  },
  'BEEHIVE-AMERICAN-LEVEL-1': {
    eval: '9780194151146',
    nonEval: '9780194660631'
  }
};

const shouldNodeBeLocked = (hasEvalLicence, node) => hasEvalLicence && node.evalOnly !== '1' && node.eval !== '1';

const shouldNodeBeHidden = (hasFullLicence, node) => hasFullLicence && node.evalOnly === '1';

const filterEvaluationModeResources = (course, hasFullLicence) => {
  const shouldBeLockedResources = []; // non eval resources are locked in evaluation mode
  const shouldBeHiddenResources = []; // evalOnly resources are hidden in normal mode

  const resourceIds = Object.keys(course.resources);
  resourceIds.forEach(resourceId => {
    if (shouldNodeBeLocked(!hasFullLicence, course.resources[resourceId])) {
      shouldBeLockedResources.push(resourceId);
      if (!course.resources[resourceId].keywords) {
        course.resources[resourceId].keywords = [];
      }
      // it means it's a non-eval resource in an evaluation mode context, that needs to be locked
      // to "tease" partial customers with what they are missing out on
      course.resources[resourceId].keywords.push(COURSE_CONSTANTS.EVALUATION_MODE_LOCKED);
    }
    if (shouldNodeBeHidden(hasFullLicence, course.resources[resourceId])) {
      if (!course.resources[resourceId].keywords) {
        course.resources[resourceId].keywords = [];
      }
      shouldBeHiddenResources.push(resourceId);
      // it means it's a eval-only resource in a normal mode context, that needs to be hidden completely
      // so full customers wouldn't question why they don't have access to it
      course.resources[resourceId].keywords.push(COURSE_CONSTANTS.NORMAL_MODE_HIDDEN);
    }
  });
  if (course.interactiveFolders) {
    const interactiveFolders = Object.values(course.interactiveFolders);
    interactiveFolders.forEach(interactiveFolder => {
      if (
        shouldBeLockedResources.includes(interactiveFolder.interactiveFolderId) ||
        shouldBeLockedResources.includes(interactiveFolder.printableResourceId) ||
        shouldBeLockedResources.includes(interactiveFolder.digitalResourceId)
      ) {
        if (!interactiveFolder.keywords) {
          interactiveFolder.keywords = [];
        }
        interactiveFolder.keywords.push(COURSE_CONSTANTS.EVALUATION_MODE_LOCKED);
      }
      if (
        shouldBeHiddenResources.includes(interactiveFolder.interactiveFolderId) ||
        shouldBeHiddenResources.includes(interactiveFolder.printableResourceId) ||
        shouldBeHiddenResources.includes(interactiveFolder.digitalResourceId)
      ) {
        if (!interactiveFolder.keywords) {
          interactiveFolder.keywords = [];
        }
        interactiveFolder.keywords.push(COURSE_CONSTANTS.NORMAL_MODE_HIDDEN);
      }
    });
  }
  return course;
};

async function getCourseById(fetch, courseId) {
  if (courseId === ENTITLEMENTS_PROP) {
    throw new Error('Not a course, do not iterate entitlements object like a course');
  }
  if (courseId === MY_OTHER_PRODUCTS_COURSE_ID) {
    // not actually a course...
    return { title: 'My other products', courseCode: 'MY-OTHER-PRODUCTS', id: courseId, isArchived: false };
  }

  const courseUrl = `${getApiBase()}/open/elt/courses/${courseId}`;

  const courseResp = await fetch(courseUrl);
  const { data } = await courseResp.json();
  return data;
}

function courseResourceIsTeacherOnly(resource) {
  if (!resource) return false;
  if (!Array.isArray(resource?.keywords)) return false;

  if (
    resource.keywords.includes(resourceFlags.teacherFlag) ||
    resource.keywords.includes(resourceFlags.teacherOnlyFlag)
  ) {
    return true;
  }

  return false;
}

function filterResources(folder, removedResourceIds = []) {
  const filteredResources = folder.resources.filter(resourceId => !removedResourceIds.includes(resourceId));
  return filteredResources;
}

function restrictUserCourses(userCourses = {}) {
  const courses = cloneDeep(userCourses);

  Object.values(courses).forEach(course => {
    if (course.userRole === selfDefinedUserTypes.CLASS_STUDENT) {
      const removedResourceIds = [];

      if (course.resources) {
        Object.values(course.resources).forEach(resource => {
          const isTeacherResource = courseResourceIsTeacherOnly(resource);
          if (isTeacherResource) {
            if (resource.resourceId) removedResourceIds.push(resource.resourceId);
            else if (Array.isArray(resource.identifier)) {
              removedResourceIds.push(...resource.identifier);
            } else if (typeof resource.identifier === 'string') {
              removedResourceIds.push(resource.identifier);
            }
          }
        });
      }

      if (removedResourceIds.length) {
        // Remove resources from Resources object
        removedResourceIds.forEach(resourceId => delete course.resources[resourceId]);

        if (course.resourceFolders) {
          // Remove resources from resourceFolders
          Object.entries(course.resourceFolders).forEach(([folderKey, folderValue]) => {
            if (folderKey === COURSE_FOLDERS.GET_STARTED || folderKey === COURSE_FOLDERS.PROFESSIONAL_DEVELOPMENT)
              return;

            if (folderValue.resources) {
              folderValue.resources = filterResources(folderValue, removedResourceIds);
            }

            const categoriesToBeRemoved = [];
            Object.entries(folderValue).forEach(([categoryName, category]) => {
              if (categoryName === FOLDER_ITEMS.RESOURCES) return;
              let filteredResources = [];
              // Filter resource ids from categories
              if (category.resources) {
                filteredResources = filterResources(category, removedResourceIds);
                if (category.resources.length !== filteredResources.length) {
                  category.resources = filteredResources;
                }
              }

              // Remove resources from subfolders
              const subFolders = Object.keys(category).filter(
                folderName =>
                  folderName !== FOLDER_ITEMS.RESOURCES &&
                  folderName !== FOLDER_ITEMS.TITLE &&
                  folderName !== FOLDER_ITEMS.KEYWORDS
              );
              const subFoldersToBeRemoved = [];

              if (subFolders.length) {
                subFolders.forEach(subFolderName => {
                  if (category[subFolderName] && category[subFolderName].resources) {
                    const filteredSubfolderResources = filterResources(category[subFolderName], removedResourceIds);
                    if (category[subFolderName].resources.length !== filteredSubfolderResources.length) {
                      category[subFolderName].resources = filteredSubfolderResources;
                    }

                    if (!filteredSubfolderResources.length) {
                      subFoldersToBeRemoved.push(subFolderName);
                    }
                  }
                });
              }

              // Remove the subfolders if there are no resources
              if (subFoldersToBeRemoved.length) {
                subFoldersToBeRemoved.forEach(subFolderName => delete category[subFolderName]);
              }

              // If all resources from a category and all subfolders were removed - remove the category itself
              if (!filteredResources.length && subFolders.length === subFoldersToBeRemoved.length) {
                categoriesToBeRemoved.push(categoryName);
              }
            });

            if (categoriesToBeRemoved.length) {
              categoriesToBeRemoved.forEach(categoryName => delete folderValue[categoryName]);
            }

            // Remove the folder if there are no categories and resources anymore
            const hasFolders = !!Object.keys(folderValue).filter(item => item !== 'resources').length;
            const hasResources = !!get(folderValue, ['resources'], []).length;
            if (!hasFolders && !hasResources) {
              delete course.resourceFolders[folderKey];
            }
          });
        }
      }
    }
  });

  return courses;
}

// Removes the assessmentFolders and interactiveFolders from the given course
// If the course doesn't have other products, it removes the course from the list as well
const removeAssessmentFromCourse = ({
  coursesForUser,
  courseId,
  assessmentIsbnWithLicence,
  licenceIsExpired,
  maxExpiryDate
}) => {
  // Remove assessment folders and interactiveFolders if user has no valid license
  delete coursesForUser.courses[courseId].assessmentFolders.assessments;
  delete coursesForUser.courses[courseId].interactiveFolders;

  // Remove the course if the there is no licence for the assessment and there are no other products
  if (!assessmentIsbnWithLicence && !coursesForUser.courses[courseId].products.ids.length) {
    delete coursesForUser.courses[courseId];
    const idIndex = coursesForUser?.ids?.indexOf(courseId);
    coursesForUser?.ids?.splice(idIndex, 1);
  } else if (licenceIsExpired) {
    // TODO: what in the hell does this mean? Why does an expired licence mean it is a TEACHER course?
    coursesForUser.courses[courseId].userRole = selfDefinedUserTypes.CLASS_TEACHER;
    coursesForUser.courses[courseId].assessmentFolders.assessments = {
      expiryDate: maxExpiryDate,
      hasLicence: false
    };
  }
  return coursesForUser;
};

function getMaxDate([...args]) {
  if (args.includes('Never')) return 'Never';

  const filteredDates = args
    .filter(date => date && Date.parse(date) && moment(date).isValid())
    .map(date => moment(date));

  const maxDate = filteredDates.length ? moment.max(filteredDates) : '';
  return maxDate;
}

// It adds the licence information to the assessments
// It could potentially remove assessments as well, if the there are no licences for them
const processAssessments = ({ coursesForUser, licencedProductIsbns, rawRedeemedExpiryDates, userAssessments }) => {
  let mutableCoursesForUser = { ...coursesForUser };

  const courseKeys = Object.keys(mutableCoursesForUser.courses);
  courseKeys.forEach(courseId => {
    if (mutableCoursesForUser?.courses?.[courseId]?.assessmentFolders?.assessments) {
      const teacherAssessmentIsbn = mutableCoursesForUser.courses[courseId].assessmentFolders.teacherIsbn;
      const assessmentIsbnWithLicence = licencedProductIsbns.find(item => item === teacherAssessmentIsbn);
      const assessmentExpiryDates = assessmentIsbnWithLicence
        ? rawRedeemedExpiryDates.filter(item => item.isbn === teacherAssessmentIsbn).map(item => item.expiryDate)
        : [];

      let maxExpiryDate;
      let licenceIsExpired = false;
      if (assessmentExpiryDates.length) {
        // If a licence doesn't have the expiry date, then it is unlimited
        maxExpiryDate = assessmentExpiryDates.some(date => date === '') ? '' : getMaxDate(assessmentExpiryDates);
        licenceIsExpired = maxExpiryDate && new Date(maxExpiryDate.toString()).getTime() < new Date().getTime();
      }

      if (!teacherAssessmentIsbn || !assessmentIsbnWithLicence || licenceIsExpired) {
        mutableCoursesForUser = removeAssessmentFromCourse({
          coursesForUser: mutableCoursesForUser,
          courseId,
          assessmentIsbnWithLicence,
          licenceIsExpired,
          maxExpiryDate
        });
      } else {
        mutableCoursesForUser.courses[courseId].userRole = selfDefinedUserTypes.CLASS_TEACHER;
        mutableCoursesForUser.courses[courseId].assessmentFolders.assessments.expiryDate = maxExpiryDate;
        mutableCoursesForUser.courses[courseId].assessmentFolders.assessments.hasLicence = true;
      }
      const assessment = userAssessments.filter(
        product => product.fields && product.fields.isbn === teacherAssessmentIsbn
      );
      if (assessmentIsbnWithLicence && assessment.length && assessment[0].fields) {
        mutableCoursesForUser.courses[courseId].assessmentFolders.assessments.title = assessment[0].fields.title;
      }
    }
  });

  return mutableCoursesForUser;
};

/**
 * remove all data from a course that is always true for any user (of a role)
 */
function optimiseCourses(courses) {
  const optimised = {};

  Object.keys(courses).forEach(courseId => {
    const course = courses[courseId];
    const userCourseProducts = courses[courseId].products.data;
    const asmtFolder = courses[courseId]?.assessmentFolders;
    const asmtProduct = asmtFolder?.assessments;

    const optimisedProducts = Object.keys(userCourseProducts).map(isbn => {
      const ucp = userCourseProducts[isbn];
      const optProduct = {
        isbn,
        started: ucp.notStartedLicence ? 0 : 1,
        licenced: ucp.hasLicence ? 1 : 0,
        expires: ucp.expiryDate === 'Never' ? 'Never' : moment(ucp.expiryDate).toISOString()
      };
      if (ucp.hasAdditionalResources) {
        optProduct.additional = 1;
      }

      if (ucp.licenceNotStartedDetails) {
        optProduct.details = ucp.licenceNotStartedDetails;
      }

      return optProduct;
    });

    optimised[courseId] = {
      courseCode: courses[courseId].courseCode || 'NO-COURSE-CODE',
      products: optimisedProducts,
      userRole: courses[courseId].userRole
    };

    if (course.fullLicence) {
      optimised[courseId].fullLicence = 1;
    } else if (course.evalLicence) {
      optimised[courseId].evalLicence = 1;
    }

    if (asmtProduct) {
      // the assessment product's details
      optimised[courseId].asmtProduct = {
        teacherIsbn: asmtFolder.teacherIsbn || null,
        studentIsbn: asmtFolder.studentIsbn || null
      };
    }
  });

  return optimised;
}

/**
 * This reverses the optimisations of optimiseCourses
 * but drawing the data in as needed (usually on the client)
 * but the same approach would apply on server too and in tests
 */
async function hydrateCourses(fetch, userCourses, userEntitlements) {
  const courses = {};

  const coursePromises = Object.keys(userCourses).map(async courseId => {
    const result = await getCourseById(fetch, courseId);
    if (result) {
      courses[courseId] = result;
      delete courses[courseId].event;
    } else {
      console.error(`Could not find course ${courseId}`);
    }
  });

  await Promise.all(coursePromises);

  const allIsbnsFromAllUserCourses = [];

  Object.keys(userCourses).forEach(courseId => {
    userCourses[courseId].products.forEach(p => {
      allIsbnsFromAllUserCourses.push(p.isbn);
    });
    const asmtTeacherIsbn = userCourses[courseId]?.asmtProduct?.teacherIsbn;
    const asmtStudentIsbn = userCourses[courseId]?.asmtProduct?.studentIsbn;
    if (asmtTeacherIsbn) {
      allIsbnsFromAllUserCourses.push(asmtTeacherIsbn);
    }
    if (asmtStudentIsbn) {
      allIsbnsFromAllUserCourses.push(asmtStudentIsbn);
    }
  });

  // get all metadata for all products (ensuring we only ask about each ISBN once)
  const products = await getProductMetadata(fetch, allIsbnsFromAllUserCourses);

  const hydrated = {};
  Object.keys(userCourses).forEach(courseId => {
    // take the entitlements which are just showing expiry etc
    const userCourse = userCourses[courseId];
    const entitlements = userCourses[courseId].products;
    const asmtEntitlement = userCourses[courseId].asmtProduct || null;

    const isbns = entitlements.map(p => p.isbn);

    // merge together the (open) course and its user specific reference (the latter only )
    hydrated[courseId] = { ...userCourses[courseId], ...courses[courseId] };

    // create a product object to store data and entitlements
    hydrated[courseId].products = {
      ids: isbns,
      data: {}
    };

    entitlements.forEach(entitlement => {
      const { isbn } = entitlement;
      const productDetails = clientFormatOfProductMetadata(products[isbn]);

      if (!productDetails) {
        console.log(`ISBN ${isbn} not available on this environment`);
        return;
      }

      delete hydrated[courseId].products.productMetadata; // this is just random rubbish from the course eltProcess / LOR's mad product entity

      const comingSoonDate = productDetails.comingSoonUntil || null;

      const readyToLaunch = !comingSoonDate || new Date(comingSoonDate) <= new Date();

      // add entitlement based properties to these products (for this user)
      if (entitlement.started) {
        productDetails.notStartedLicence = false;
      } else {
        productDetails.notStartedLicence = true;
        productDetails.licenceNotStartedDetails = entitlement.details || {};
      }
      productDetails.expiryDate = entitlement.expires;
      productDetails.hasLicence = Boolean(entitlement.licenced);
      productDetails.notReadyToLaunch = null; // this is a bug in the current data - it is always null, left to keep unit tests happy
      productDetails.readyToLaunch = readyToLaunch; // this, meanwhile can be trusted if anyone cares to use it

      // this is a course property being added to a product - makes no sense, right?
      // but just in case of people whining about the breaking of this illogical
      // category error / data modelling issue, we honour it
      productDetails.hasAdditionalResources = Boolean(entitlement.additional);

      // set the data against the ISBN in this course
      hydrated[courseId].products.data[isbn] = productDetails;

      // set the damnable assessment ISBN in its assessment folder - seriously, who modelled this nightmare? H R Giger?
      // this forces there is be exactly 0 or 1 assessment products in a course - but why?
      // and why treat them differently to other products in this way?
    });
    const asmtProduct = hydrated[courseId]?.assessmentFolders?.assessments;
    if (
      asmtProduct &&
      asmtEntitlement &&
      userEntitlements?.licencedProducts?.indexOf(asmtEntitlement.teacherIsbn) !== -1
    ) {
      asmtProduct.expiryDate = asmtEntitlement.expires;
      asmtProduct.hasLicence = Boolean(asmtEntitlement.licenced);
      asmtProduct.title = products[asmtEntitlement.teacherIsbn]?.title || 'Unknown';

      // as per the equivalent line of getHubCourses, this makes no sense at all even if we assume role means usertype
      // but for exact equivalence we need to keep it
      hydrated[courseId].userRole = selfDefinedUserTypes.CLASS_TEACHER;
    }
    if (userCourse.fullLicence) {
      hydrated[courseId] = filterEvaluationModeResources(hydrated[courseId], true);
    } else if (userCourse.evalLicence) {
      // "else if" because we only want to filter the course if either fullLicence is set or evalLicence
      // and these will be set only on "eval courses"
      hydrated[courseId] = filterEvaluationModeResources(hydrated[courseId], false);
    }
  });
  // Remove assessments folders if the user does not have access
  const coursesWithAssessmentProductsProcessed = processAssessments({
    coursesForUser: { courses: hydrated },
    licencedProductIsbns: userEntitlements.licencedProducts,
    rawRedeemedExpiryDates: userEntitlements.redeemedExpiryDates,
    userAssessments: userEntitlements.assessmentProducts
  });

  // Filter resources from the reducedCourses
  const restrictedUserCourses = restrictUserCourses(coursesWithAssessmentProductsProcessed.courses);
  return restrictedUserCourses;
}

function _getCourseLevel(course) {
  if (!course || !course.properties) return '';
  const { firstCourseLevel = '', secondCourseLevel = '', courseLevel = '' } = course.properties;

  if (courseLevel) return courseLevel;
  if (firstCourseLevel && secondCourseLevel) return `${firstCourseLevel} ${secondCourseLevel}`;
  if (firstCourseLevel && !secondCourseLevel) return firstCourseLevel;

  return '';
}

function getSortedCourseIds(courses = {}) {
  const ids = Object.keys(courses);

  if (!ids.length) {
    return [];
  }

  return ids.sort((a, b) => {
    // My Other Products appears last in the list, always
    if (courses[a].id === MY_OTHER_PRODUCTS_COURSE_ID) return 1;
    if (courses[b].id === MY_OTHER_PRODUCTS_COURSE_ID) return -1;
    return (
      courses[a].title.localeCompare(courses[b].title, undefined, { numeric: true }) ||
      `${courses[a].title} ${_getCourseLevel(courses[a])}`.localeCompare(
        `${courses[b].title} ${_getCourseLevel(courses[b])}`,
        undefined,
        { numeric: true }
      )
    );
  });
}

module.exports = {
  shouldNodeBeLocked,
  optimiseCourses,
  hydrateCourses,
  getCourseById,
  _getCourseLevel,
  restrictUserCourses,
  getMaxDate,
  processAssessments,
  filterResources,
  getSortedCourseIds,
  shouldNodeBeHidden,
  specialPackIsbns,
  filterEvaluationModeResources,
  COURSE_CONSTANTS,
  MY_OTHER_PRODUCTS_COURSE_ID,
  COURSES_PROP,
  ENTITLEMENTS_PROP,
  removeAssessmentFromCourse,
  courseResourceIsTeacherOnly
};
